import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { createSelectors } from '@/utils/createSelectors'

const STEPS = [
	'LOCATION',
	'ROOM_CONFIG',
	'TRAVEL_PERIOD',
	'ROOM_SELECTION',
	'GUEST_DATA',
	'PAYMENT',
] as const

export type Step = (typeof STEPS)[number]

type StepState = {
	currentStep: Step
	summarySteps: Step[]
	setCurrentStep: (step: Step) => void
	setNextStep: () => void
	setSummarySteps: (step: Step) => void
	resetSteps: (step: Step[]) => void
	setStepForce: (step: Step, summary: Step[]) => void
}

const useStepStoreBase = create<StepState, [['zustand/devtools', never]]>(
	devtools(
		(set) => ({
			currentStep: STEPS[0],
			summarySteps: [],
			setNextStep: () => {
				set(
					(state) => {
						const currentStepIndex = STEPS.findIndex(
							(step) => step === state.currentStep
						)
						if (currentStepIndex + 1 >= STEPS.length) return state

						return {
							...state,
							currentStep: STEPS[currentStepIndex + 1],
							summarySteps: [...state.summarySteps, state.currentStep],
						}
					},
					false,
					'setNextStep'
				)
			},
			setCurrentStep: (step: Step) =>
				set(
					(state) => {
						const isInSummerySteps = state.summarySteps.find((s) => s === step)
						const stepIndex = STEPS.findIndex((s) => s === step)
						const prevStep = STEPS[stepIndex - 1]

						const canBeNavigated =
							isInSummerySteps ||
							Boolean(state.summarySteps.find((s) => s === prevStep))

						if (stepIndex > 0 && !canBeNavigated) return state

						return {
							...state,
							currentStep: step,
						}
					},
					false,
					'setCurrentStep'
				),
			setSummarySteps: (step: Step) =>
				set(
					(state) => ({ ...state, summarySteps: [...state.summarySteps, step] }),
					false,
					'setSummarySteps'
				),
			resetSteps: (steps: Step[]) =>
				set(
					(state) => {
						return {
							...state,
							summarySteps: state.summarySteps.filter((s) => !steps.includes(s)),
						}
					},
					false,
					'resetSteps'
				),
			setStepForce: (step: Step, summary: Step[]) =>
				set(
					(state) => {
						return {
							...state,
							currentStep: step,
							summarySteps: summary,
						}
					},
					false,
					'setStepForce'
				),
		}),
		{ name: 'name', store: 'StepStore' }
	)
)

export const useStepStore = createSelectors(useStepStoreBase)
